import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import {
  SlotButton,
  HeaderTitle,
  ActionButton,
  SelectedChip,
  ContainerGrid,
  StyledSummary,
  StyledAccordion,
} from './TimeSlotAccordionPicker.styles';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const groupSlotsByDay = (slots) => {
  const map = {};

  slots.forEach((slot) => {
    if (!map[slot.dayOfWeek]) map[slot.dayOfWeek] = [];
    map[slot.dayOfWeek].push(slot);
  });

  return Object.entries(map).sort(
    ([a], [b]) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b),
  );
};

const getDaySelectionState = (daySlots, selectedIds) => {
  const ids = daySlots.map((s) => s.timeSlotId);
  const selectedCount = ids.filter((id) => selectedIds.includes(id)).length;

  return {
    ids,
    selectedCount,
    allSelected: selectedCount === ids.length,
  };
};

const TimeSlotButton = React.memo(({ slot, isSelected, onToggle }) => (
  <Grid item xs={12} sm={6} md={4} lg={3}>
    <SlotButton
      fullWidth
      size="small"
      variant="outlined"
      onClick={() => onToggle(slot.timeSlotId)}
      className={isSelected ? 'selected-slot' : ''}
    >
      {slot.startTimeFmt} – {slot.endTimeFmt}
    </SlotButton>
  </Grid>
));

const DayAccordion = ({
  day,
  slots,
  selectedIds,
  onToggleSlot,
  onToggleDay,
}) => {
  const { ids, selectedCount, allSelected } = getDaySelectionState(
    slots,
    selectedIds,
  );

  return (
    <StyledAccordion disableGutters>
      <StyledSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container alignItems="center">
          <Grid item xs>
            <HeaderTitle>{capitalize(day)}</HeaderTitle>
          </Grid>

          {selectedCount > 0 && (
            <SelectedChip
              label={`${selectedCount}/${ids.length} selected`}
              size="small"
              color="primary"
            />
          )}

          <ActionButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleDay(slots, allSelected);
            }}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </ActionButton>
        </Grid>
      </StyledSummary>

      <ContainerGrid container spacing={1}>
        {slots.map((slot) => (
          <TimeSlotButton
            key={slot.timeSlotId}
            slot={slot}
            isSelected={selectedIds.includes(slot.timeSlotId)}
            onToggle={onToggleSlot}
          />
        ))}
      </ContainerGrid>
    </StyledAccordion>
  );
};

export const TimeSlotAccordionPicker = ({
  timeSlots = [],
  selectedIds = [],
  onChange,
}) => {
  const groupedSlots = useMemo(() => groupSlotsByDay(timeSlots), [timeSlots]);

  const toggleSlot = useCallback(
    (id) => {
      const updated = selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id];

      onChange(updated);
    },
    [selectedIds, onChange],
  );

  const toggleDay = useCallback(
    (slots, allSelected) => {
      const ids = slots.map((s) => s.timeSlotId);

      const updated = allSelected
        ? selectedIds.filter((id) => !ids.includes(id))
        : [...new Set([...selectedIds, ...ids])];

      onChange(updated);
    },
    [selectedIds, onChange],
  );

  const selectAll = (e) => {
    e.stopPropagation();
    onChange(timeSlots.map((s) => s.timeSlotId));
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  if (!timeSlots.length) return null;

  return (
    <StyledAccordion disableGutters>
      {/* Header */}
      <StyledSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container alignItems="center">
          <Grid item xs>
            <HeaderTitle>
              Allowed Time Slots{' '}
              <Typography
                fontSize="1rem"
                component="span"
                color="text.secondary"
              >
                (Optional — leave empty for no restriction)
              </Typography>
            </HeaderTitle>
          </Grid>

          <ActionButton size="small" onClick={selectAll}>
            Select All
          </ActionButton>

          <ActionButton size="small" onClick={clearAll}>
            Clear All
          </ActionButton>
        </Grid>
      </StyledSummary>

      {/* Days */}
      {groupedSlots.map(([day, slots]) => (
        <DayAccordion
          key={day}
          day={day}
          slots={slots}
          selectedIds={selectedIds}
          onToggleSlot={toggleSlot}
          onToggleDay={toggleDay}
        />
      ))}
    </StyledAccordion>
  );
};

TimeSlotAccordionPicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  timeSlots: PropTypes.array.isRequired,
  selectedIds: PropTypes.array.isRequired,
};
